import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { hook } from '@g4/automation-hooks';
import Link from '@g4/next-furniture/Link';
import { ConditionalWrap, Box, useMedia } from '../../Boneyard';
import tracking from '@g4/g4-analytics';

import { HEADER_MERCHANDISE_BANNER } from '../../../../automationHooks.json';

const propTypes = {
  banner: PropTypes.shape({
    image: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    mobileDescription: PropTypes.string,
    title: PropTypes.string,
    color: PropTypes.string,
    clickThrough: PropTypes.shape({
      url: PropTypes.string,
      target: PropTypes.string,
    }),
  }),
};

const Banner = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  min-height: 60px;
  flex-grow: 1;
  background-image: linear-gradient(
    ${props => props.theme.colors.siteHeader.topBarBackground.from},
    ${props => props.theme.colors.siteHeader.topBarBackground.to}
  );
  background-color: ${props => props.bgColor ?? 'transparent'};
  padding: ${props => props.theme.spacing(props.isMobile ? 1 : 2)};

  ${props => props.theme.query.md} {
    min-height: 20px;
    background-image: none;
    padding: 0;
  }
`;

const Image = styled.img`
  height: 44px;
  width: auto;
  margin-right: ${props => props.theme.spacing(props.isMobile ? 1 : 2)};
  padding: ${props => props.theme.spacing(props.isMobile ? 0 : 1)};
`;

//used <div> instead of Text component because inner html can have <div>s, so it should not be wrapped in a <span>
const BannerText = styled.div`
  color: white;
  font-size: ${props => (props.isMobile ? '10px' : '12px')};
  ${props => props.theme.query.lg} {
    font-size: 16px;
  }
`;

const MerchandiseBanner = ({ banner }) => {
  const mq = useMedia();
  const isMobile = mq.lt('sm');

  React.useEffect(() => {
    const impressionData = {
      name: banner.title,
      creative: banner.image.split('/').slice(-1)[0],
      position: 'Top Nav Banner',
    };
    tracking.trackPromoImpressionExternal(impressionData);
  }, [banner.title, banner.image]);

  const trackPromoClick = () => {
    const impressionData = {
      name: banner.title,
      creative: banner.image.split('/').slice(-1)[0],
      position: `Top Nav Banner`,
    };
    tracking.trackPromoClickExternal(impressionData);
  };

  return (
    <ConditionalWrap
      condition={banner.clickThrough?.url}
      wrap={children => (
        <Box flex="1">
          <Link
            href={banner.clickThrough?.url}
            target={
              banner.clickThrough?.target
                ? `_${banner.clickThrough.target.toLowerCase()}`
                : '_blank'
            }
          >
            {({ anchorProps }) => (
              <a
                {...anchorProps}
                {...hook(HEADER_MERCHANDISE_BANNER.hook, 'link')}
                onClick={trackPromoClick}
              >
                {children}
              </a>
            )}
          </Link>
        </Box>
      )}
    >
      <Banner
        {...hook(HEADER_MERCHANDISE_BANNER.hook)}
        bgColor={banner.color}
        isMobile={isMobile}
      >
        <Image
          src={banner.image}
          alt="banner icon"
          isMobile={isMobile}
          {...hook(HEADER_MERCHANDISE_BANNER.hook, 'image')}
        />
        {mq.lt('md') && banner.mobileDescription ? (
          <Box>
            <BannerText
              isMobile={isMobile}
              {...hook(HEADER_MERCHANDISE_BANNER.hook, 'description')}
              dangerouslySetInnerHTML={{ __html: banner.mobileDescription }}
            ></BannerText>
          </Box>
        ) : (
          <Box mr={mq.lt('md') ? 0 : 2}>
            <BannerText
              {...hook(HEADER_MERCHANDISE_BANNER.hook, 'description')}
              dangerouslySetInnerHTML={{ __html: banner.description }}
            ></BannerText>
          </Box>
        )}
      </Banner>
    </ConditionalWrap>
  );
};

MerchandiseBanner.propTypes = propTypes;

export default withTheme(MerchandiseBanner);
